import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from 'firebase/firestore';
import { db } from './firebase';

const STORAGE_EVENT = 'maiCollegesUserStorageChange';
const PREFIX = 'maicolleges:user:';
const DIRTY_PREFIX = 'maicolleges:dirty:';
const LEGACY_PREFIX = 'maicollege:user:';
const LEGACY_DIRTY_PREFIX = 'maicollege:dirty:';
const CLOUD_WRITE_DELAY = 400;

let activeUid = null;
let unsubscribe = null;
const pendingCloudWrites = new Map();

function scopedPrefix(uid = activeUid) {
  return uid ? `${PREFIX}${uid}:` : '';
}

function scopedLegacyPrefix(uid = activeUid) {
  return uid ? `${LEGACY_PREFIX}${uid}:` : '';
}

function scopedKey(key) {
  return activeUid ? `${scopedPrefix()}${key}` : null;
}

function dirtyKey(uid, key) {
  return `${DIRTY_PREFIX}${uid}:${encodeURIComponent(key)}`;
}

function readDirtyOperation(uid, key) {
  return localStorage.getItem(dirtyKey(uid, key));
}

function markDirty(uid, key, operation) {
  localStorage.setItem(dirtyKey(uid, key), operation);
}

function clearDirtyIfCurrent(uid, key, operation, value) {
  if (readDirtyOperation(uid, key) !== operation) return;
  const currentValue = localStorage.getItem(`${scopedPrefix(uid)}${key}`);
  if ((operation === 'set' && currentValue === value) || (operation === 'delete' && currentValue === null)) {
    localStorage.removeItem(dirtyKey(uid, key));
  }
}

function cloudDocument(uid, key) {
  return doc(db, 'users', uid, 'appData', encodeURIComponent(key));
}

function emitChange(key, value) {
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT, { detail: { key, value } }));
}

function collectLocalEntries(prefix) {
  const entries = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(prefix)) {
      entries.push([key.slice(prefix.length), localStorage.getItem(key)]);
    }
  }
  return entries;
}

function localEntries(uid = activeUid) {
  if (!uid) return [];
  return collectLocalEntries(scopedPrefix(uid));
}

function legacyLocalEntries(uid = activeUid) {
  if (!uid) return [];
  return collectLocalEntries(scopedLegacyPrefix(uid));
}

function migrateLegacyUserStorage(uid) {
  const oldPrefix = scopedLegacyPrefix(uid);
  const newPrefix = scopedPrefix(uid);
  const legacyEntries = collectLocalEntries(oldPrefix);

  legacyEntries.forEach(([key, value]) => {
    const newKey = `${newPrefix}${key}`;
    const oldKey = `${oldPrefix}${key}`;
    if (localStorage.getItem(newKey) === null && value !== null) {
      localStorage.setItem(newKey, value);
    }
    localStorage.removeItem(oldKey);
  });

  const oldDirtyPrefix = `${LEGACY_DIRTY_PREFIX}${uid}:`;
  const newDirtyPrefix = `${DIRTY_PREFIX}${uid}:`;
  const legacyDirtyKeys = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const storageKey = localStorage.key(index);
    if (storageKey?.startsWith(oldDirtyPrefix)) legacyDirtyKeys.push(storageKey);
  }

  legacyDirtyKeys.forEach((oldKey) => {
    const suffix = oldKey.slice(oldDirtyPrefix.length);
    const newKey = `${newDirtyPrefix}${suffix}`;
    const value = localStorage.getItem(oldKey);
    if (localStorage.getItem(newKey) === null && value !== null) {
      localStorage.setItem(newKey, value);
    }
    localStorage.removeItem(oldKey);
  });
}

function queueCloudWrite(uid, key, operation, value = null) {
  const queueKey = `${uid}:${key}`;
  const existingTimer = pendingCloudWrites.get(queueKey);
  if (existingTimer) clearTimeout(existingTimer);

  const timer = setTimeout(async () => {
    pendingCloudWrites.delete(queueKey);
    try {
      if (operation === 'delete') {
        await deleteDoc(cloudDocument(uid, key));
      } else {
        await setDoc(cloudDocument(uid, key), { key, value, updatedAt: Date.now() });
      }
      clearDirtyIfCurrent(uid, key, operation, value);
    } catch (error) {
      console.error(`Unable to sync account data key "${key}":`, error);
    }
  }, CLOUD_WRITE_DELAY);

  pendingCloudWrites.set(queueKey, timer);
}

async function syncDirtyEntries(uid) {
  const prefix = `${DIRTY_PREFIX}${uid}:`;
  const dirtyEntries = [];
  for (let index = 0; index < localStorage.length; index += 1) {
    const storageKey = localStorage.key(index);
    if (!storageKey?.startsWith(prefix)) continue;
    dirtyEntries.push([
      decodeURIComponent(storageKey.slice(prefix.length)),
      localStorage.getItem(storageKey),
    ]);
  }

  await Promise.allSettled(dirtyEntries.map(async ([key, operation]) => {
    const value = localStorage.getItem(`${scopedPrefix(uid)}${key}`);
    if (operation === 'delete') {
      await deleteDoc(cloudDocument(uid, key));
    } else if (value !== null) {
      await setDoc(cloudDocument(uid, key), { key, value, updatedAt: Date.now() });
    }
    clearDirtyIfCurrent(uid, key, operation, value);
  }));
}

export function hasLocalUserStorage(uid) {
  return Boolean(uid) && (localEntries(uid).length > 0 || legacyLocalEntries(uid).length > 0);
}

export function initializeUserStorage(uid) {
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  activeUid = uid || null;
  if (!activeUid) return Promise.resolve({ source: 'none' });

  const initializedUid = activeUid;
  migrateLegacyUserStorage(initializedUid);
  const appData = collection(db, 'users', initializedUid, 'appData');

  return new Promise((resolve) => {
    let initialSnapshotHandled = false;
    let initialResultSettled = false;
    const settleInitial = (result) => {
      if (initialResultSettled) return;
      initialResultSettled = true;
      resolve(result);
    };

    unsubscribe = onSnapshot(appData, (snapshot) => {
      if (activeUid !== initializedUid) {
        settleInitial({ source: 'cancelled' });
        return;
      }

      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();
        const key = data.key;
        if (typeof key !== 'string') return;
        if (readDirtyOperation(initializedUid, key)) return;
        const fullKey = `${scopedPrefix(initializedUid)}${key}`;
        if (change.type === 'removed') {
          if (localStorage.getItem(fullKey) !== null) {
            localStorage.removeItem(fullKey);
            emitChange(key, null);
          }
        } else if (typeof data.value === 'string') {
          if (localStorage.getItem(fullKey) !== data.value) {
            localStorage.setItem(fullKey, data.value);
            emitChange(key, data.value);
          }
        }
      });

      if (!initialSnapshotHandled) {
        initialSnapshotHandled = true;
        settleInitial({ source: 'cloud' });
        void syncDirtyEntries(initializedUid);
      }
    }, (error) => {
      console.error('Account data synchronization stopped; using local cache:', error);
      settleInitial({ source: 'local', error });
    });
  });
}

export function clearActiveUserStorage() {
  if (unsubscribe) unsubscribe();
  unsubscribe = null;
  activeUid = null;
}

export function subscribeToUserStorage(callback) {
  const handler = (event) => callback(event.detail);
  window.addEventListener(STORAGE_EVENT, handler);
  return () => window.removeEventListener(STORAGE_EVENT, handler);
}

export const userStorage = {
  getItem(key) {
    const fullKey = scopedKey(key);
    return fullKey ? localStorage.getItem(fullKey) : null;
  },

  setItem(key, value) {
    const fullKey = scopedKey(key);
    if (!fullKey || !activeUid) return;
    const stringValue = String(value);
    if (localStorage.getItem(fullKey) === stringValue) return;
    const uid = activeUid;
    localStorage.setItem(fullKey, stringValue);
    markDirty(uid, key, 'set');
    queueCloudWrite(uid, key, 'set', stringValue);
  },

  removeItem(key) {
    const fullKey = scopedKey(key);
    if (!fullKey || !activeUid) return;
    const uid = activeUid;
    localStorage.removeItem(fullKey);
    markDirty(uid, key, 'delete');
    queueCloudWrite(uid, key, 'delete');
  },

  key(index) {
    return localEntries()[index]?.[0] || null;
  },

  get length() {
    return localEntries().length;
  },
};
